/* IRRELEVANT — welcome popup + display-currency converter
 *
 * Currency: display-only conversion (checkout stays in USD, the store
 * currency). Rates are pulled live from open.er-api.com, with the jsDelivr
 * currency-api as a fallback if that provider is down or rate-limits us.
 * They are cached in localStorage and re-checked every RATES_TTL (1h) —
 * on load, on an interval while the tab is open, and whenever the tab is
 * refocused. Cached rates are shown instantly and refreshed in the
 * background (stale-while-revalidate), so a slow provider never blocks
 * the page or leaves a price blank.
 *
 * Conversion works by scanning text nodes for USD prices ($xx.xx), wrapping
 * them in <span class="ir-money" data-usd="…">, and re-rendering those spans
 * whenever the selected currency changes or the DOM updates (cart drawer
 * re-renders are picked up by a MutationObserver).
 */
(function () {
  'use strict';

  var BASE = 'USD';
  var KEY_RATES = 'ir_fx_v2';
  var KEY_CUR = 'ir_currency';
  var KEY_SEEN = 'ir_welcome_seen_v1';
  var SEEN_DAYS = 30;

  /* How often we re-check the providers for new rates.
   * NOTE: the free feeds below publish one new snapshot per day, so setting
   * this below ~1h buys no extra accuracy and risks being rate-limited.
   * If you move to a paid intraday feed, drop this to 60 * 1000 for 1 min. */
  var RATES_TTL = 60 * 60 * 1000; // 1h
  /* Cached rates older than this are considered unusable -> fall back to USD. */
  var RATES_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7d

  var RATE_SOURCES = [
    {
      url: 'https://open.er-api.com/v6/latest/USD',
      pick: function (d) {
        return d && d.rates;
      },
    },
    {
      url: 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json',
      pick: function (d) {
        if (!d || !d.usd) return null;
        var out = {};
        Object.keys(d.usd).forEach(function (k) {
          out[k.toUpperCase()] = d.usd[k];
        });
        return out;
      },
    },
  ];

  var PRICE_RE = /\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g;
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1, OPTION: 1, IFRAME: 1 };

  var rates = null;
  var ratesFetchedAt = 0;
  var inflight = null;
  var current = BASE;
  var observer = null;
  var mutating = false;

  function store(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode — ignore */
    }
  }

  function read(key) {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (e) {
      return null;
    }
  }

  /* ——— rates ——— */

  /* Guard against a provider returning an error page, a partial payload, or
   * a shape we don't recognise — a bad table here is what produced NaN before. */
  function ratesLookSane(r) {
    return !!(r && r.EUR && r.GBP && r.AED && r.QAR && Object.keys(r).length > 20);
  }

  function fetchFrom(i) {
    if (i >= RATE_SOURCES.length) return Promise.resolve(null);
    var source = RATE_SOURCES[i];
    return fetch(source.url, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var got = source.pick(data);
        if (!ratesLookSane(got)) throw new Error('unusable payload');
        return got;
      })
      .catch(function () {
        return fetchFrom(i + 1); // try the next provider
      });
  }

  function refreshRates() {
    if (inflight) return inflight; // never stack requests
    inflight = fetchFrom(0).then(function (got) {
      inflight = null;
      if (got) {
        rates = got;
        ratesFetchedAt = Date.now();
        store(KEY_RATES, { t: ratesFetchedAt, rates: rates });
        renderMoney(); // repaint with the newer numbers
      }
      return rates;
    });
    return inflight;
  }

  function loadRates() {
    if (rates === null) {
      var cached = read(KEY_RATES);
      if (cached && ratesLookSane(cached.rates) && Date.now() - cached.t < RATES_MAX_AGE) {
        rates = cached.rates;
        ratesFetchedAt = cached.t;
      }
    }
    if (rates && Date.now() - ratesFetchedAt < RATES_TTL) return Promise.resolve(rates);
    if (rates) {
      refreshRates(); // stale-while-revalidate: paint now, update when it lands
      return Promise.resolve(rates);
    }
    return refreshRates();
  }

  /* Keep checking for new rates for as long as the tab is open. */
  function keepRatesFresh() {
    setInterval(function () {
      if (!document.hidden) loadRates();
    }, RATES_TTL);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) loadRates();
    });
  }

  function formatMoney(usd, code) {
    if (code === BASE || !rates || !rates[code]) {
      return '$' + usd.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    var value = usd * rates[code];
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'code',
      }).format(value);
    } catch (e) {
      return code + ' ' + Math.round(value).toLocaleString('en');
    }
  }

  /* ——— DOM conversion ——— */

  function wrapPrices(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue || node.nodeValue.indexOf('$') === -1) return NodeFilter.FILTER_REJECT;
        var el = node.parentElement;
        if (!el || SKIP_TAGS[el.tagName]) return NodeFilter.FILTER_REJECT;
        if (el.closest('.ir-money, [data-ir-no-convert]')) return NodeFilter.FILTER_REJECT;
        PRICE_RE.lastIndex = 0;
        return PRICE_RE.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(function (node) {
      var text = node.nodeValue;
      var frag = document.createDocumentFragment();
      var last = 0;
      var m;
      PRICE_RE.lastIndex = 0;
      while ((m = PRICE_RE.exec(text))) {
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        var span = document.createElement('span');
        span.className = 'ir-money';
        span.setAttribute('data-usd', m[1].replace(/,/g, ''));
        span.textContent = m[0];
        frag.appendChild(span);
        last = m.index + m[0].length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
  }

  function renderMoney() {
    document.querySelectorAll('.ir-money').forEach(function (span) {
      var usd = parseFloat(span.getAttribute('data-usd'));
      if (!isNaN(usd)) span.textContent = formatMoney(usd, current);
    });
    document.querySelectorAll('[data-ir-cur-label]').forEach(function (el) {
      el.textContent = current;
    });
  }

  function convertAll() {
    mutating = true;
    wrapPrices(document.body);
    renderMoney();
    requestAnimationFrame(function () {
      mutating = false;
    });
  }

  function watch() {
    if (observer) return;
    var scheduled = false;
    observer = new MutationObserver(function () {
      if (mutating || scheduled || current === BASE) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        convertAll();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function setCurrency(code) {
    current = code;
    store(KEY_CUR, code);
    document.querySelectorAll('[data-ir-currency-select]').forEach(function (sel) {
      if (sel.value !== code) sel.value = code;
    });
    if (code === BASE) {
      renderMoney(); // restore $ display on already-wrapped spans
    } else {
      loadRates().then(function () {
        convertAll();
        watch();
      });
    }
  }

  /* ——— welcome gate ——— */

  var KEY_COUNTRY = 'ir_country';

  function selectCountry(popup, iso) {
    var sel = popup.querySelector('[data-ir-country-select]');
    if (!sel || !iso) return;
    var opt = sel.querySelector('option[value="' + iso.toUpperCase() + '"]');
    if (opt) sel.value = opt.value;
  }

  function updateFooterCountry(name) {
    if (!name) return;
    document.querySelectorAll('[data-ir-footer-country]').forEach(function (el) {
      el.textContent = name;
    });
  }

  function detectCountry(popup) {
    var savedCountry = read(KEY_COUNTRY);
    if (savedCountry) {
      selectCountry(popup, savedCountry);
      var savedOpt = popup.querySelector('option[value="' + savedCountry + '"]');
      if (savedOpt) updateFooterCountry(savedOpt.getAttribute('data-name'));
      return; // an explicit choice beats detection
    }

    fetch('/browsing_context_suggestions.json?country%5Benabled%5D=true')
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var detected = data && data.detected_values;
        if (!detected || !detected.country || !detected.country.handle) return;
        selectCountry(popup, detected.country.handle);
        updateFooterCountry(detected.country_name);
      })
      .catch(function () {
        /* Lebanon preselected server-side as fallback */
      });
  }

  function confirmGate(popup) {
    var sel = popup.querySelector('[data-ir-country-select]');
    var opt = sel && sel.options[sel.selectedIndex];
    if (opt) {
      store(KEY_COUNTRY, opt.value);
      updateFooterCountry(opt.getAttribute('data-name'));
      var cur = opt.getAttribute('data-currency');
      if (cur) setCurrency(cur);
    }
    closePopup(popup);
  }

  var lockedScrollY = 0;

  function openPopup(popup) {
    detectCountry(popup);
    popup.hidden = false;
    lockedScrollY = window.scrollY || 0;
    document.documentElement.classList.add('ir-welcome-open');
    document.body.classList.add('ir-welcome-open');
    document.body.style.top = -lockedScrollY + 'px';
    requestAnimationFrame(function () {
      popup.classList.add('is-open');
    });
  }

  function closePopup(popup) {
    popup.classList.remove('is-open');
    document.documentElement.classList.remove('ir-welcome-open');
    document.body.classList.remove('ir-welcome-open');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
    store(KEY_SEEN, Date.now());
    setTimeout(function () {
      popup.hidden = true;
    }, 250);
  }

  function initPopup() {
    var popup = document.getElementById('IrWelcome');
    if (!popup) return;

    popup.querySelectorAll('[data-ir-welcome-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        closePopup(popup);
      });
    });

    popup.querySelectorAll('[data-ir-welcome-confirm]').forEach(function (el) {
      el.addEventListener('click', function () {
        confirmGate(popup);
      });
    });

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-ir-welcome-open]');
      if (opener) {
        event.preventDefault();
        openPopup(popup);
      }
    });

    var seen = read(KEY_SEEN);
    var expired = !seen || Date.now() - seen > SEEN_DAYS * 24 * 60 * 60 * 1000;
    if (expired) {
      setTimeout(function () {
        openPopup(popup);
      }, 700);
    }
  }

  /* ——— boot ——— */

  function init() {
    initPopup();
    keepRatesFresh();

    // Delegated: any [data-ir-currency-select] (header, mobile menu, popup)
    // drives the converter and all of them stay in sync via setCurrency.
    document.addEventListener('change', function (event) {
      var sel = event.target.closest ? event.target.closest('[data-ir-currency-select]') : null;
      if (sel) setCurrency(sel.value);
    });

    var saved = read(KEY_CUR);
    if (saved && saved !== BASE) {
      setCurrency(saved);
    } else {
      loadRates(); // warm the daily cache in the background
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
